import * as React from "react";
import { useTheme } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

interface Option {
  label: string;
  value: string;
}

interface CommonDropDownProps {
  label?: string;
  placeholder?: string;
  options: Option[]; // 🟢 Array of { label, value }
  value: string; // Only the selected value (i.e., value: "6839af..." )
  onChange: (value: string) => void;
  width?: number | string;
  height?: number | string;
  margin?: number | string;
  required?: boolean;
  error?: boolean; // ✅ Add this
  helperText?: string;
  menuWidth?: number | string; // ✅ new prop
  enableSearch?: boolean;
  searchValue?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const CommonDropDown: React.FC<CommonDropDownProps> = ({
  label,
  placeholder = "Select",
  options,
  value,
  onChange,
  width,
  height,
  margin,
  required,
  error,
  helperText,
  menuWidth, // ✅ new prop
  enableSearch = false,
  searchValue = "",
  onSearch,
  searchPlaceholder = "Search",
  onLoadMore,
  hasMore = false,
  loading = false,
  disabled = false,
}) => {
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  const handleMenuScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!enableSearch || !onLoadMore || !hasMore || loading) return;
    const target = event.currentTarget;
    const reachedBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 24;
    if (reachedBottom) {
      onLoadMore();
    }
  };

  const compactMenuLabels = [
    "Recent Enquiries",
    "Monthly Profit",
    "Profit Trend Chart",
    "Revenue Pie Type",
    "Revenue Line Type",
  ];
  const resolvedMenuWidth =
    menuWidth ?? (compactMenuLabels.includes(label || "") ? 120 : undefined);

  return (
    <FormControl
      sx={{ m: margin ?? 1, width: width || 300, height:"100%" ,justifyContent:'space-between'}}
    >
      {label && (
        <label
          style={{
            fontSize: "0.875rem",
            color: "#1e293b", // Tailwind's slate-800
            marginBottom: "4px",
            display: "block",
          }}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <Select
        displayEmpty
        fullWidth
        value={value}
        onChange={handleChange}
        disabled={disabled}
        input={<OutlinedInput />}
        IconComponent={KeyboardArrowDownIcon}
        renderValue={(selected) => {
          const selectedOption = options.find((opt) => opt.value === selected);
          return selectedOption ? selectedOption.label : <em>{placeholder}</em>;
        }}
        MenuProps={{
          ...(enableSearch
            ? { autoFocus: false, disableAutoFocusItem: true }
            : {}),
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: {
            style: {
              maxHeight: enableSearch
                ? 280
                : label !== "CategoryList"
                ? 38 * 4.5 + 8
                : 38 * 4.5 + 18,
              ...(resolvedMenuWidth != null ? { width: resolvedMenuWidth } : {}),
            },
            sx: {
              maxWidth: "none",
              "& .MuiMenuItem-root": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                lineHeight: 1.35,
                py: 1,
              },
            },
            onScroll: enableSearch ? handleMenuScroll : undefined,
          },
        }}
        inputProps={{ "aria-label": "Without label" }}
        sx={{
          fontSize: "0.875rem",
          height: height,
          "& .MuiSelect-select": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      >
        {enableSearch && (
          <ListSubheader
            sx={{
              bgcolor: "#fff",
              lineHeight: "unset",
              py: 1,
              zIndex: 1,
              whiteSpace: "normal",
            }}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearch?.(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </ListSubheader>
        )}
        <MenuItem disabled value="">
          <em>{placeholder}</em>
        </MenuItem>
        {options.map((opt: Option, idx: number) => (
          <MenuItem key={idx} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
        {enableSearch && loading && options.length === 0 && (
          <MenuItem disabled>
            <em>Loading...</em>
          </MenuItem>
        )}
        {enableSearch && !loading && options.length === 0 && (
          <MenuItem disabled>
            <em>No options found</em>
          </MenuItem>
        )}
        {enableSearch && loading && options.length > 0 && (
          <MenuItem disabled sx={{ justifyContent: "center" }}>
            <CircularProgress size={18} />
          </MenuItem>
        )}
      </Select>
      {error && helperText && (
        <span className="text-xs text-red-700 mt-1">{helperText}</span>
      )}
    </FormControl>
  );
};

export default CommonDropDown;
